import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

export interface RetryConfig {
  maxRetries: number;
  delay: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RetryService {
  private defaultConfig: RetryConfig = {
    maxRetries: 3,
    delay: 1000,
    backoffMultiplier: 2,
    retryCondition: (error) => this.shouldRetryError(error),
  };

  retryWithBackoff<T>(
    source: Observable<T>,
    config: Partial<RetryConfig> = {}
  ): Observable<T> {
    const finalConfig = { ...this.defaultConfig, ...config };

    return source.pipe(
      retry({
        count: finalConfig.maxRetries,
        delay: (error, retryCount) => {
          if (!finalConfig.retryCondition!(error)) {
            return throwError(() => error);
          }

          const delay =
            finalConfig.delay *
            Math.pow(finalConfig.backoffMultiplier || 2, retryCount - 1);

          console.log(
            `Retrying request in ${delay}ms (attempt ${retryCount}/${finalConfig.maxRetries})`
          );

          return timer(delay);
        },
      })
    );
  }

  private shouldRetryError(error: any): boolean {
    if (error?.status === 0) {
      return true;
    }

    if (error?.status >= 500 && error?.status < 600) {
      return true;
    }

    if (error?.name === 'TimeoutError') {
      return true;
    }

    if (
      error?.message?.includes('Network') ||
      error?.message?.includes('Connection') ||
      error?.message?.includes('Timeout')
    ) {
      return true;
    }

    return false;
  }

  getRetryConfigForError(error: any): RetryConfig {
    if (error?.status === 0) {
      return {
        maxRetries: 5,
        delay: 500,
        backoffMultiplier: 1.5,
        retryCondition: () => true,
      };
    }

    if (error?.status >= 500) {
      return {
        maxRetries: 3,
        delay: 1000,
        backoffMultiplier: 2,
        retryCondition: () => true,
      };
    }

    if (error?.status === 429) {
      return {
        maxRetries: 3,
        delay: 5000,
        backoffMultiplier: 2,
        retryCondition: () => true,
      };
    }

    return this.defaultConfig;
  }

  isRetryableError(error: any): boolean {
    return this.shouldRetryError(error);
  }
}
